import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate } from "react-router-dom";
import LeagueStandings from "./LeagueStandings.js";
import LeagueRegistration from "./LeagueRegistration.js";
import LeagueLocations from "./LeagueLocations.js";
import LeagueGames from "./LeagueGames.js";
import CreateLeagueWizard from "./CreateLeagueWizard.js";
import { Drawer } from "./common/Drawer.js";
import FilterRadioPopover from "./common/FilterRadioPopover.js";
import { setHeaderTitle } from "./Header.js";
import { fetchData } from "../utils/api.js";
import { ScheduleTabs } from "./common/ScheduleTabs.js";
import { Button } from "./ui/Button.js";

const LEAGUE_TABS = ["Standings", "Registration", "Locations"];
const DEFAULT_GENDER_OPTIONS = ["Men's", "Women's", "Co-ed"];
const DEFAULT_DIVISION_OPTIONS = [
  "Copper",
  "Bronze",
  "Silver",
  "Gold",
  "Platinum",
];

const classifyRosterLeagues = (userRosters, teams, seasons, leagues) => {
  const coachLeagues = [];
  const playerLeagues = [];
  const fanLeagues = [];

  userRosters.forEach((r) => {
    const team = teams.find((t) => t.team_id === r.team_id);
    if (!team) return;
    const season = seasons.find((s) => s.season_id === team.season_id);
    if (!season) return;
    const league = leagues.find(
      (l) => (l.league_id || l.id) === season.league_id,
    );
    if (!league) return;

    const roleLower = (r.role || "").toLowerCase();
    if (roleLower === "coach" || roleLower === "captain") {
      coachLeagues.push(league);
    } else if (roleLower === "player") {
      playerLeagues.push(league);
    } else if (roleLower === "fan") {
      fanLeagues.push(league);
    }
  });

  return { coachLeagues, playerLeagues, fanLeagues };
};

const loadRosterLeagueData = async (uid) => {
  const userRosters =
    (await fetchData(`/roster_details?firebase_id=eq.${uid}`)) || [];
  if (userRosters.length === 0) {
    return {
      coachLeagues: [],
      playerLeagues: [],
      fanLeagues: [],
      teamIds: [],
      coachTeams: [],
    };
  }

  const teamIds = [
    ...new Set(userRosters.map((r) => r.team_id).filter(Boolean)),
  ];
  if (teamIds.length === 0) {
    return {
      coachLeagues: [],
      playerLeagues: [],
      fanLeagues: [],
      teamIds: [],
      coachTeams: [],
    };
  }

  const teams =
    (await fetchData(`/teams?team_id=in.(${teamIds.join(",")})`)) || [];
  const coachTeamIds = new Set(
    userRosters
      .filter((r) =>
        ["coach", "captain"].includes((r.role || "").toLowerCase()),
      )
      .map((r) => String(r.team_id)),
  );
  const coachTeams = teams.filter((t) =>
    coachTeamIds.has(String(t.team_id || t.id)),
  );

  const seasonIds = [...new Set(teams.map((t) => t.season_id).filter(Boolean))];
  if (seasonIds.length === 0) {
    return {
      coachLeagues: [],
      playerLeagues: [],
      fanLeagues: [],
      teamIds,
      coachTeams,
    };
  }

  const seasons =
    (await fetchData(`/seasons?season_id=in.(${seasonIds.join(",")})`)) || [];
  const leagueIds = [
    ...new Set(seasons.map((s) => s.league_id).filter(Boolean)),
  ];
  if (leagueIds.length === 0) {
    return {
      coachLeagues: [],
      playerLeagues: [],
      fanLeagues: [],
      teamIds,
      coachTeams,
    };
  }

  const leagues =
    (await fetchData(`/leagues?league_id=in.(${leagueIds.join(",")})`)) || [];
  return {
    ...classifyRosterLeagues(userRosters, teams, seasons, leagues),
    teamIds,
    coachTeams,
  };
};

const computePrimaryRole = (l) => {
  if (l.isAdmin) return { role: "Admin", uiStatus: "admin" };
  if (l.isUmpire) return { role: "Umpire", uiStatus: "umpire" };
  if (l.isCoach) return { role: "Coach", uiStatus: "coach" };
  if (l.isPlayer) return { role: "Player", uiStatus: "player" };
  if (l.isFan) return { role: "Fan", uiStatus: "fan" };
  return { role: "Member", uiStatus: "member" };
};

const determineUserLeagueRole = async (userObj, targetId) => {
  if (!userObj) return { role: "Guest", uiStatus: "guest" };
  if (userObj.is_super_admin) return { role: "Admin", uiStatus: "admin" };

  const fbUid = userObj.firebaseUid || userObj.uid;
  if (fbUid) {
    try {
      const managed = await fetchData(
        `/leagues_managed?league_id=eq.${targetId}&firebase_id=eq.${encodeURIComponent(fbUid)}`,
      );
      if (managed?.length > 0) return { role: "Admin", uiStatus: "admin" };

      const umpiring = await fetchData(
        `/leagues_umpiring?league_id=eq.${targetId}&firebase_id=eq.${encodeURIComponent(fbUid)}`,
      );
      if (umpiring?.length > 0) return { role: "Umpire", uiStatus: "umpire" };
    } catch (e) {
      console.warn("Error checking managed/umpiring role:", e);
    }
  }

  const dbUserId = userObj.user_id;
  if (dbUserId && Number.isInteger(Number(dbUserId))) {
    try {
      const roles = await fetchData(
        `/league_roles?league_id=eq.${targetId}&user_id=eq.${dbUserId}`,
      );
      if (roles?.length > 0) {
        const r = roles[0].role || "Admin";
        return { role: r, uiStatus: r.toLowerCase() };
      }
    } catch (e) {
      console.warn("Error checking league_roles:", e);
    }
  }
  return { role: "Guest", uiStatus: "guest" };
};

const LeagueDashboard = ({ user }) => {
  const { leagueId } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Standings");
  const [participatingLeagues, setParticipatingLeagues] = useState([]);
  const [publicLeagues, setPublicLeagues] = useState([]);
  const [userTeamIds, setUserTeamIds] = useState([]);
  const [coachTeams, setCoachTeams] = useState([]);
  const [selectedLeagueOverrideId, setSelectedLeagueOverrideId] =
    useState(null);
  const [selectedSeasonId, setSelectedSeasonId] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [seasonsRefreshTick, setSeasonsRefreshTick] = useState(0);
  const [showCreateLeague, setShowCreateLeague] = useState(false);

  const fetchUserSpecificData = useCallback(async (uid) => {
    try {
      const {
        coachLeagues,
        playerLeagues,
        fanLeagues,
        teamIds,
        coachTeams: loadedCoachTeams,
      } = await loadRosterLeagueData(uid);
      setUserTeamIds(teamIds);
      setCoachTeams(loadedCoachTeams);

      const umpireLeagues =
        (await fetchData(`/leagues_umpiring?firebase_id=eq.${uid}`)) || [];
      const managedLeagues =
        (await fetchData(`/leagues_managed?firebase_id=eq.${uid}`)) || [];

      const allLeaguesMap = new Map();
      [
        ...coachLeagues,
        ...playerLeagues,
        ...fanLeagues,
        ...umpireLeagues,
        ...managedLeagues,
      ].forEach((l) => allLeaguesMap.set(l.league_id || l.id, l));

      const hasLeagueId = (arr, target) =>
        arr.some((item) => (item.league_id || item.id) === target);

      setParticipatingLeagues(
        Array.from(allLeaguesMap.values()).map((l) => {
          const lid = l.league_id || l.id;
          return {
            ...l,
            id: lid,
            isUmpire: hasLeagueId(umpireLeagues, lid),
            isCoach: hasLeagueId(coachLeagues, lid),
            isPlayer: hasLeagueId(playerLeagues, lid),
            isFan: hasLeagueId(fanLeagues, lid),
            isAdmin: hasLeagueId(managedLeagues, lid),
          };
        }),
      );
    } catch (err) {
      console.error("Error fetching user data:", err);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      setIsLoading(true);
      try {
        if (user?.firebaseUid) {
          await fetchUserSpecificData(user.firebaseUid);
        } else {
          setParticipatingLeagues([]);
          const pub = await fetchData("/leagues").catch(() => []);
          if (!cancelled && Array.isArray(pub)) {
            setPublicLeagues(
              pub.map((l) => ({
                ...l,
                id: l.league_id || l.id,
                userRole: "Guest",
                uiStatus: "guest",
              })),
            );
          }
        }
      } catch (err) {
        console.error("Error loading dashboard data:", err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    void loadData();
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.firebaseUid, fetchUserSpecificData]);

  const myLeagues = useMemo(() => {
    const rolePriority = {
      Admin: 1,
      Umpire: 2,
      Coach: 3,
      Player: 4,
      Fan: 5,
      Member: 6,
    };
    const arr = participatingLeagues.map((l) => {
      const roles = [];
      if (l.isAdmin) roles.push("Admin");
      if (l.isUmpire) roles.push("Official");
      if (l.isCoach) roles.push("Coach");
      if (l.isPlayer) roles.push("Player");
      if (l.isFan) roles.push("Fan");
      if (roles.length === 0) roles.push("Member");
      const { role, uiStatus } = computePrimaryRole(l);
      return { ...l, userRole: role, userRoles: roles, uiStatus };
    });
    arr.sort(
      (a, b) =>
        (rolePriority[a.userRole] || 8) - (rolePriority[b.userRole] || 8),
    );
    return arr;
  }, [participatingLeagues]);

  const availableLeagues = useMemo(
    () => (myLeagues.length > 0 ? myLeagues : publicLeagues),
    [myLeagues, publicLeagues],
  );

  const selectedLeague = useMemo(() => {
    if (leagueId) {
      return availableLeagues.find((l) => String(l.id) === String(leagueId));
    }
    if (selectedLeagueOverrideId) {
      return availableLeagues.find(
        (l) => String(l.id) === String(selectedLeagueOverrideId),
      );
    }
    return null;
  }, [leagueId, selectedLeagueOverrideId, availableLeagues]);

  const [viewingLeague, setViewingLeague] = useState(null);
  const [leagueSeasons, setLeagueSeasons] = useState([]);
  const [isLoadingUrlLeague, setIsLoadingUrlLeague] = useState(
    Boolean(leagueId),
  );

  const targetLeagueId =
    viewingLeague?.league_id ||
    viewingLeague?.id ||
    selectedLeague?.id ||
    availableLeagues[0]?.id;

  useEffect(() => {
    if (!targetLeagueId) {
      setLeagueSeasons([]);
      return undefined;
    }
    let cancelled = false;
    const loadSeasons = async () => {
      try {
        const sData = await fetchData(
          `/seasons?league_id=eq.${targetLeagueId}`,
        );
        if (!cancelled && Array.isArray(sData)) {
          setLeagueSeasons(sData);
        }
      } catch (err) {
        console.error("Failed to load seasons", err);
      }
    };
    void loadSeasons();
    return () => {
      cancelled = true;
    };
  }, [targetLeagueId, seasonsRefreshTick]);

  useEffect(() => {
    if (leagueSeasons.length === 0) {
      setSelectedSeasonId("");
      return;
    }
    const stillExists = leagueSeasons.some(
      (s) => String(s.season_id || s.id) === String(selectedSeasonId),
    );
    if (!stillExists) {
      const firstId = leagueSeasons[0].season_id || leagueSeasons[0].id;
      setSelectedSeasonId(firstId ? String(firstId) : "");
    }
  }, [leagueSeasons, selectedSeasonId]);

  useEffect(() => {
    const loadLeagueFromUrl = async () => {
      if (!leagueId) {
        setViewingLeague(null);
        setIsLoadingUrlLeague(false);
        return;
      }
      if (selectedLeague) {
        setViewingLeague(selectedLeague);
        setIsLoadingUrlLeague(false);
        return;
      }
      setIsLoadingUrlLeague(true);
      try {
        const leagues = await fetchData(`/leagues?league_id=eq.${leagueId}`);
        const found = leagues?.length > 0 ? leagues[0] : null;
        const { role, uiStatus } = found
          ? await determineUserLeagueRole(user, leagueId)
          : { role: "Guest", uiStatus: "guest" };

        setViewingLeague(
          found
            ? {
                ...found,
                id: found.league_id || found.id,
                isAdmin: role === "Admin" || role === "admin",
                userRole: role,
                uiStatus,
              }
            : { id: leagueId, notFoundOrLoading: true },
        );
      } catch (error) {
        console.error("Failed to fetch specific league:", error);
        setViewingLeague({ id: leagueId, notFoundOrLoading: true });
      } finally {
        setIsLoadingUrlLeague(false);
      }
    };
    void loadLeagueFromUrl();
  }, [leagueId, selectedLeague, user]);

  const currentLeague = useMemo(() => {
    if (viewingLeague && !viewingLeague.notFoundOrLoading) {
      return viewingLeague;
    }
    if (selectedLeague) return selectedLeague;
    return availableLeagues[0] || null;
  }, [viewingLeague, selectedLeague, availableLeagues]);

  useEffect(() => {
    if (!currentLeague) return;
    const gList =
      Array.isArray(currentLeague.genders) && currentLeague.genders.length > 0
        ? currentLeague.genders.filter((g) => g !== "Open")
        : DEFAULT_GENDER_OPTIONS;
    const dList =
      Array.isArray(currentLeague.divisions) &&
      currentLeague.divisions.length > 0
        ? currentLeague.divisions
        : DEFAULT_DIVISION_OPTIONS;
    setSelectedGender((prev) => (gList.includes(prev) ? prev : gList[0] || ""));
    setSelectedDivision((prev) =>
      dList.includes(prev) ? prev : dList[0] || "",
    );
  }, [currentLeague]);

  useEffect(() => {
    if (viewingLeague && !viewingLeague.notFoundOrLoading) {
      setHeaderTitle(viewingLeague.name);
    } else {
      setHeaderTitle("");
    }
    return () => setHeaderTitle("");
  }, [viewingLeague]);

  if (
    (isLoading && availableLeagues.length === 0) ||
    (leagueId && isLoadingUrlLeague && !selectedLeague)
  ) {
    return <div className="loading-spinner" />;
  }

  const handleLeagueSwitch = (nextId) => {
    if (leagueId) {
      navigate(`/league/${nextId}`);
    } else {
      setSelectedLeagueOverrideId(nextId);
    }
  };

  const renderDetailView = (league) => {
    const isLeagueAdmin = Boolean(
      user &&
      (league.userRole === "Admin" ||
        league.userRole === "admin" ||
        league.isAdmin ||
        user.is_super_admin),
    );

    const visibleTabs = isLeagueAdmin ? [...LEAGUE_TABS, "Games"] : LEAGUE_TABS;
    const effectiveTab = visibleTabs.includes(activeTab)
      ? activeTab
      : "Standings";

    const activeSeasonObj =
      leagueSeasons.find(
        (s) => String(s.season_id || s.id) === String(selectedSeasonId),
      ) ||
      leagueSeasons[0] ||
      null;

    const leagueChoices =
      availableLeagues.length > 0 ? availableLeagues : [league];
    const genderOptions =
      Array.isArray(league.genders) && league.genders.length > 0
        ? league.genders.filter((g) => g !== "Open")
        : DEFAULT_GENDER_OPTIONS;
    const divisionOptions =
      Array.isArray(league.divisions) && league.divisions.length > 0
        ? league.divisions
        : DEFAULT_DIVISION_OPTIONS;

    const filterSections = [
      {
        id: "league",
        label: "League",
        options: leagueChoices.map((l) => ({
          value: String(l.league_id || l.id),
          label: l.name || "Unnamed League",
        })),
        selectedValue: String(league.league_id || league.id),
        onSelect: handleLeagueSwitch,
      },
      {
        id: "season",
        label: "Season",
        options: leagueSeasons.map((s) => ({
          value: String(s.season_id || s.id),
          label:
            s.name ||
            [s.season_type || s.season, s.year].filter(Boolean).join(" ") ||
            "Season",
        })),
        selectedValue: String(selectedSeasonId),
        onSelect: setSelectedSeasonId,
      },
      {
        id: "gender",
        label: "Gender",
        options: genderOptions.map((g) => ({
          value: g,
          label: g,
          showMedallion: true,
        })),
        selectedValue: selectedGender,
        onSelect: setSelectedGender,
      },
      {
        id: "division",
        label: "Division",
        options: divisionOptions.map((d) => ({
          value: d,
          label: d,
          showMedallion: true,
        })),
        selectedValue: selectedDivision,
        onSelect: setSelectedDivision,
      },
    ];

    return (
      <div
        className="league-detail-view single-layer-scroll"
        style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}
      >
        <div id="league-section-info" style={{ margin: "0.25rem 0 0" }}>
          <h2
            style={{
              margin: 0,
              fontSize: "1.75rem",
              fontWeight: 600,
              color: "var(--color-text-primary)",
            }}
          >
            {league.name || "Unnamed League"}
          </h2>
        </div>

        <div
          className="wireframe-subtab-bar"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <ScheduleTabs
              tabs={visibleTabs}
              activeTab={effectiveTab}
              onTabChange={setActiveTab}
            />
          </div>
          <div style={{ marginBottom: "20px" }}>
            <FilterRadioPopover
              sections={filterSections}
              footerAction={
                user
                  ? {
                      label: "Create League",
                      onClick: () => setShowCreateLeague(true),
                    }
                  : null
              }
              ariaLabel="League filters"
            />
          </div>
        </div>

        {effectiveTab === "Standings" && (
          <div id="league-section-standings">
            <LeagueStandings
              league={league}
              selectedSeason={activeSeasonObj}
              selectedGender={selectedGender}
              selectedDivision={selectedDivision}
              userTeamIds={userTeamIds}
            />
          </div>
        )}

        {effectiveTab === "Registration" && (
          <div id="league-section-registration">
            <LeagueRegistration
              league={league}
              seasons={leagueSeasons}
              selectedSeason={activeSeasonObj}
              user={user}
              isLeagueAdmin={isLeagueAdmin}
              coachTeams={coachTeams}
              onSeasonsChanged={() => setSeasonsRefreshTick((n) => n + 1)}
              onRegistered={(teamId) => {
                if (teamId) {
                  setUserTeamIds((prev) =>
                    prev.includes(teamId) ? prev : [...prev, teamId],
                  );
                }
              }}
            />
          </div>
        )}

        {effectiveTab === "Locations" && (
          <div id="league-section-locations">
            <LeagueLocations league={league} user={user} />
          </div>
        )}

        {effectiveTab === "Games" && isLeagueAdmin && (
          <div id="league-section-games">
            <LeagueGames
              league={league}
              selectedSeason={activeSeasonObj}
              isLeagueAdmin={isLeagueAdmin}
              user={user}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="league-dashboard">
      {!currentLeague && (
        <div
          className="empty-state"
          style={{
            padding: "2rem 0",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "1rem",
          }}
        >
          <p style={{ margin: 0, color: "var(--color-text-secondary)" }}>
            {user
              ? "You aren't part of any leagues yet."
              : "No leagues available yet."}
          </p>
          {user && (
            <Button
              variant="primary"
              type="button"
              onClick={() => setShowCreateLeague(true)}
            >
              Create League
            </Button>
          )}
        </div>
      )}

      {currentLeague && renderDetailView(currentLeague)}

      <Drawer
        isOpen={showCreateLeague}
        onClose={() => setShowCreateLeague(false)}
        title="Create League"
      >
        <CreateLeagueWizard
          user={user}
          onClose={() => setShowCreateLeague(false)}
          onComplete={async (created) => {
            setShowCreateLeague(false);
            if (user?.firebaseUid) {
              await fetchUserSpecificData(user.firebaseUid);
            }
            const newId = created?.league_id || created?.id;
            if (newId) {
              setSelectedLeagueOverrideId(newId);
            }
          }}
        />
      </Drawer>
    </div>
  );
};

LeagueDashboard.propTypes = {
  user: PropTypes.object,
};

export default LeagueDashboard;
